import type { RiskGapModel } from '@ctw/shared/api/fhir/models/risk-gap';
import { Badge } from '@ctw/shared/components/badge';
import type { PatientRiskGapsTableColumn } from '@ctw/shared/content/patient-risk-gaps/components/resource-table';
import { confidenceLevelToRating } from '@ctw/shared/content/risk/risk-gap-confidence-level';
import { RiskGapInfoIcon } from '@ctw/shared/content/risk/risk-gap-info-icon';
import { riskGapStatusBadgeDefinitionV2 } from '@ctw/shared/content/risk/risk-gap-status-badge-definition-v2';
import { riskGapTypeBadgeDefinition } from '@ctw/shared/content/risk/risk-gap-type-badge-definition';
import { formatFHIRDate } from '@ctw/shared/utils/dates';
import { tw } from '@ctw/shared/utils/tailwind';
import { startCase } from 'lodash-es';

export const buildPatientRiskGapsColumnsV2 = (): Array<
  PatientRiskGapsTableColumn<RiskGapModel>
> => [
  {
    title: 'HCC',
    render: (model, hasSelectedRow) => {
      const title = hasSelectedRow
        ? model.title.length > 86
          ? `${model.title.slice(0, 86)}…`
          : model.title
        : model.title;

      return (
        <div className={tw`flex flex-row items-center justify-between`}>
          <div className={tw`flex flex-col font-semibold text-sm`}>
            {title} {`(HCC ${model.hccCode})`}
          </div>
        </div>
      );
    },
    minimizedClassName: `${tw`order-3 mb-2 w-full p-0`}`,
    className: `${tw`order-3 mb-2 max-w-auto p-0 hover:underline md:order-1 md:mb-0 md:max-w-[600px] md:p-4`}`,
  },
  {
    title: 'Type',
    render: (model) => {
      return (
        <Badge
          size={'sm'}
          {...riskGapTypeBadgeDefinition(model.type)}
          content={startCase(model.type)}
        />
      );
    },
    minimizedClassName: `${tw`order-1 mb-2 w-fit p-0`}`,
    className: `${tw`order-1 mb-2 w-fit p-0 md:order-2 md:mb-0 md:p-4`}`,
  },
  {
    title: 'Status',
    render: (model) => (
      <Badge
        size={'sm'}
        {...riskGapStatusBadgeDefinitionV2(model)}
        icon={undefined}
        className={tw`rounded-full border bg-white text-content-main`}
      />
    ),
    minimizedClassName: `${tw`order-2 mb-2 w-fit p-0`}`,
    className: `${tw`order-2 mb-2 w-fit p-0 md:order-3 md:mb-0 md:p-4`}`,
  },
  {
    title: 'Confidence',
    render: (model) => {
      const level = confidenceLevelToRating(model.confidenceLevel);
      const display = model.type === 'suspect' && level ? startCase(level) : '—';
      return (
        <div
          className={tw`hidden flex-col overflow-hidden text-ellipsis whitespace-nowrap text-sm md:flex`}
        >
          {display}
        </div>
      );
    },
    minimizedClassName: `${tw`hidden`}`,
    className: `${tw`mb-2 w-fit p-0 md:mb-0 md:p-4`}`,
  },
  {
    title: 'Last Updated',
    render: (model, shouldMinimize) => {
      const hasFirstPartyData = model.icd10Codes.some((code) =>
        code.dataSourceType.includes('first-party'),
      );

      const lastDiagnosisDate = model.icd10Codes
        .map((code) => code.lastEncounterDiagnosisRecordedDate)
        .filter(Boolean)
        .at(-1);

      const showInfoIcon = shouldMinimize && hasFirstPartyData;
      return (
        <div
          className={tw`flex overflow-hidden text-ellipsis whitespace-nowrap pt-1 text-content-subtle text-xs`}
        >
          <RiskGapInfoIcon
            hide={!showInfoIcon}
            info={'First Party Data'}
            className={tw`mr-2 flex items-center`}
          />
          {lastDiagnosisDate
            ? `Encounter Diagnosis Found ${formatFHIRDate(lastDiagnosisDate)}`
            : undefined}
        </div>
      );
    },
    minimizedClassName: `${tw`order-4 w-fit p-0`}`,
    className: `${tw`order-4 w-fit p-0 md:mb-0 md:p-4`}`,
  },
];
